import MD5 from 'crypto-js/md5';
import { xatAceFrameImageUrl } from '@/services/api-paths';

export const ACES_STORE_URL = 'https://xat.com/aces';

export type AceAvatarFrame = {
  id: string;
  name: string;
  shape?: 'square' | 'round';
  /** Imagem enviada pelo painel (caminho em /public ou data URL). */
  imageUrl?: string;
  /** @deprecated Legado — fallback CDN/wiki para seeds antigos. */
  cdnFile?: string;
  cdnAlternates?: string[];
  wikiFile?: string;
};

export const VAF_AVATAR_FRAMES: AceAvatarFrame[] = [
  {
    id: 'heartaurea',
    name: 'HeartAurea',
    shape: 'round',
    cdnFile: 'heartaurea',
    cdnAlternates: ['heartround', 'heartaurearound'],
    wikiFile: 'Heartaurea.png',
  },
  { id: 'heartbloom', name: 'HeartBloom', shape: 'square', cdnFile: 'heartbloom', wikiFile: 'HeartBloom.png' },
  { id: 'heartcloud', name: 'HeartCloud', shape: 'square', cdnFile: 'heartcloud', wikiFile: 'HeartCloud.png' },
  { id: 'heartgarden', name: 'HeartGarden', shape: 'square', cdnFile: 'heartgarden', wikiFile: 'HeartGarden.png' },
  { id: 'heartlocked', name: 'HeartLocked', shape: 'square', cdnFile: 'heartlocked', wikiFile: 'HeartLocked.png' },
  { id: 'heartpunk', name: 'HeartPunk', shape: 'square', cdnFile: 'heartpunk', wikiFile: 'HeartPunk.png' },
  { id: 'heartthorns', name: 'HeartThorns', shape: 'square', cdnFile: 'heartthorns', wikiFile: 'HeartThorns.png' },
];

export const XAF_AVATAR_FRAMES: AceAvatarFrame[] = [
  {
    id: 'christmas',
    name: 'Christmas',
    shape: 'square',
    cdnFile: 'christmas',
    cdnAlternates: ['xafchristmas', 'xaf1'],
    wikiFile: 'Christmas.png',
  },
  {
    id: 'frost',
    name: 'Frost',
    shape: 'square',
    cdnFile: 'frost',
    cdnAlternates: ['xaffrost', 'xaf2'],
    wikiFile: 'Frost.png',
  },
];

export function aceAvatarFramesForSlug(slug: string): AceAvatarFrame[] | null {
  switch (slug) {
    case 'vaf':
      return VAF_AVATAR_FRAMES;
    case 'xaf':
      return XAF_AVATAR_FRAMES;
    default:
      return null;
  }
}

export function emptyAvatarFrame(): AceAvatarFrame {
  return {
    id: `frame-${Date.now().toString(36)}`,
    name: 'Nova moldura',
    shape: 'square',
    imageUrl: '',
  };
}

/** URL exibida na galeria — prioriza upload do painel. */
export function aceFrameImageSrc(frame: AceAvatarFrame): string {
  const uploaded = frame.imageUrl?.trim();
  if (uploaded) return uploaded;
  return aceFrameProxyUrl(frame);
}

/** Resolve molduras do Ace: CMS tem prioridade; fallback para dados curados por slug. */
export function resolveAceAvatarFrames(
  ace: Pick<AceInfoLike, 'slug' | 'avatarFramesEnabled' | 'avatarFrames'>
): AceAvatarFrame[] | null {
  if (ace.avatarFramesEnabled === false) return null;
  if (ace.avatarFrames && ace.avatarFrames.length > 0) return ace.avatarFrames;
  if (ace.avatarFramesEnabled) return null;
  return aceAvatarFramesForSlug(ace.slug);
}

type AceInfoLike = {
  slug: string;
  avatarFramesEnabled?: boolean;
  avatarFrames?: AceAvatarFrame[];
};

export function aceFrameProxyUrl(frameOrId: string | AceAvatarFrame): string {
  if (typeof frameOrId === 'string') {
    return xatAceFrameImageUrl(frameOrId);
  }
  return xatAceFrameImageUrl(frameOrId.id);
}

function wikiImageUrl(filename: string, host: 'https://xat.wiki' | 'https://wiki.xat.com'): string {
  const hex = MD5(filename).toString();
  return `${host}/w/images/${hex[0]}/${hex.substring(0, 2)}/${encodeURIComponent(filename)}`;
}

export function aceFrameImageCandidates(frame: AceAvatarFrame): string[] {
  const files = [frame.cdnFile, ...(frame.cdnAlternates ?? [])].filter(
    (file): file is string => Boolean(file)
  );

  const urls: string[] = [];

  for (const file of files) {
    for (const base of [
      'https://xat.com/images/avframes/',
      'https://xath.hu/images/avframes/',
    ]) {
      for (const ext of ['webp', 'png', 'gif']) {
        urls.push(`${base}${file}.${ext}`);
      }
    }
  }

  if (frame.wikiFile) {
    urls.push(
      `https://xat.wiki/wiki/Special:FilePath/${encodeURIComponent(frame.wikiFile)}`,
      `https://wiki.xat.com/wiki/Special:FilePath/${encodeURIComponent(frame.wikiFile)}`,
      wikiImageUrl(frame.wikiFile, 'https://xat.wiki'),
      wikiImageUrl(frame.wikiFile, 'https://wiki.xat.com')
    );
  }

  return Array.from(new Set(urls));
}

/** Resolve molduras de um power (CMS). */
export function resolvePowerAvatarFrames(
  power: Pick<PowerAvatarFramesLike, 'slug' | 'avatarFramesEnabled' | 'avatarFrames'>
): AceAvatarFrame[] | null {
  if (power.avatarFramesEnabled && power.avatarFrames && power.avatarFrames.length > 0) {
    return power.avatarFrames;
  }
  return null;
}

type PowerAvatarFramesLike = {
  slug: string;
  avatarFramesEnabled?: boolean;
  avatarFrames?: AceAvatarFrame[];
};
